// @systeme: sondages
// ============================================================================
// CECODEV Platform - Page publique de réponse à un sondage
// ============================================================================
// Route : /sondage/:token (pas d'authentification)
// Le participant reçoit un lien avec token et remplit le sondage.
// Intérêt = Oui / Non (simple). Dispo = OUI/NON/PEUT-ÊTRE par créneau.
// ============================================================================

import { useState, useEffect, Fragment } from "react";
import { API_URL, messageErreur } from "../lib/api";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  LinearProgress,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { Calendar, CheckCircle, XCircle, HelpCircle, Send, ThumbsUp, ThumbsDown } from "lucide-react";
import { splitCreneaux } from "../components/SondageForm";
import { toast } from "sonner";
import { formatDateFR, formatJourSemaineFR } from "@cecodev/shared";


// ============================================================================
// Types
// ============================================================================

interface Creneau {
  id: string;
  dateCreneau: string;
  heureDebut: string;
  heureFin: string;
  lieu: string | null;
  notes: string | null;
  ordre: number;
  nbOui: number;
  nbNon: number;
  nbPeutEtre: number;
}

type ReponseValue = "OUI" | "NON" | "PEUT_ETRE";

// ============================================================================
// Component
// ============================================================================

export default function SondagePublicPage() {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sondage, setSondage] = useState<any>(null);
  const [destinataire, setDestinataire] = useState<any>(null);
  const [creneaux, setCreneaux] = useState<Creneau[]>([]);
  const [cloture, setCloture] = useState(false);
  const [expire, setExpire] = useState(false);
  const [message, setMessage] = useState("");

  // Réponses
  const [reponses, setReponses] = useState<Record<string, ReponseValue>>({});
  const [interetOui, setInteretOui] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ============================================================================
  // Load data
  // ============================================================================

  useEffect(() => {
    if (!token) return;

    fetch(`${API_URL}/sondages/repondre/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Lien invalide");
        }
        return res.json();
      })
      .then((data) => {
        setSondage(data.sondage);
        setDestinataire(data.destinataire);
        setCreneaux(data.creneaux || []);
        setCloture(data.cloture || false);
        setExpire(data.expire || false);
        setMessage(data.message || "");

        // Charger les réponses existantes
        if (data.reponsesExistantes) {
          const existingMap: Record<string, ReponseValue> = {};
          (data.reponsesExistantes as Array<{ creneauId: string; reponse: ReponseValue }>).forEach((r) => {
            existingMap[r.creneauId] = r.reponse;
          });
          setReponses(existingMap);
        }
        if (data.reponsesComplementaires) {
          // niveauInteret >= 3 → oui, sinon non
          const niveau = data.reponsesComplementaires.niveauInteret;
          if (niveau != null) {
            setInteretOui(niveau >= 3);
          }
        }
      })
      .catch((err) => {
        setError(err.message || "Lien invalide ou expiré");
      })
      .finally(() => setLoading(false));
  }, [token]);

  // ============================================================================
  // Submit
  // ============================================================================

  const handleSubmit = async () => {
    // Validate: all creneaux must have a response
    if (creneaux.length > 0) {
      const missing = creneaux.filter((c) => !reponses[c.id]);
      if (missing.length > 0) {
        toast.error(`Veuillez répondre à tous les créneaux (${missing.length} manquant${missing.length > 1 ? "s" : ""})`);
        return;
      }
    }

    // Validate intérêt if asked
    if (sondage?.sonderInteret && interetOui === null) {
      toast.error("Veuillez indiquer si vous êtes intéressé(e).");
      return;
    }

    try {
      setSubmitting(true);

      const payload: any = {};

      // Réponses aux créneaux
      if (creneaux.length > 0) {
        payload.reponses = creneaux.map((c) => ({
          creneauId: c.id,
          reponse: reponses[c.id],
        }));
      }

      // Intérêt as oui/non (stored as niveauInteret 5=oui, 1=non)
      if (sondage?.sonderInteret && interetOui !== null) {
        payload.interet = {
          niveauInteret: interetOui ? 5 : 1,
          commentaire: undefined,
        };
      }

      const res = await fetch(`${API_URL}/sondages/repondre/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Erreur lors de l'envoi");
      }

      setSubmitted(true);
    } catch (err) {
      toast.error(messageErreur(err) || "Erreur lors de l'envoi des réponses");
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================================
  // Toggle response
  // ============================================================================

  const toggleReponse = (creneauId: string, value: ReponseValue) => {
    setReponses((prev) => ({ ...prev, [creneauId]: value }));
  };

  // ============================================================================
  // Render states
  // ============================================================================

  const progress = creneaux.length > 0
    ? Math.round((Object.keys(reponses).length / creneaux.length) * 100)
    : 0;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
        <CircularProgress sx={{ color: "#4BB7A5" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
        <Card sx={{ maxWidth: 500, mx: 2 }}>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <XCircle size={48} color="#f44336" />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
              Lien invalide
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              {error}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
        <Card sx={{ maxWidth: 500, mx: 2 }}>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <CheckCircle size={48} color="#4caf50" />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
              Merci !
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Vos réponses ont été enregistrées avec succès.
            </Typography>
            {destinataire?.aRepondu && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Vous pouvez fermer cette page.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (cloture || expire) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
        <Card sx={{ maxWidth: 500, mx: 2 }}>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <Calendar size={48} color="#ff9800" />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
              {cloture ? "Sondage clôturé" : "Date limite dépassée"}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              {message}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ============================================================================
  // Main form
  // ============================================================================

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa", pb: 4 }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#4BB7A5", color: "white", py: 4, px: 3, textAlign: "center" }}>
        <Calendar size={36} color="white" />
        <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
          {sondage?.titre}
        </Typography>
        {sondage?.groupeNom && (
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            {sondage.groupeNom}
          </Typography>
        )}
      </Box>

      <Box sx={{ maxWidth: 700, mx: "auto", px: 2, mt: -2 }}>
        {/* Welcome card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body1">
              Bonjour <strong>{destinataire?.prenom} {destinataire?.nom}</strong>,
            </Typography>
            {sondage?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {sondage.description}
              </Typography>
            )}
            {sondage?.dateLimiteReponse && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Date limite de réponse :{" "}
                <strong>
                  {formatDateFR(sondage.dateLimiteReponse)}
                </strong>
              </Typography>
            )}
            {destinataire?.aRepondu && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Vous avez déjà répondu à ce sondage. Vous pouvez modifier vos réponses ci-dessous.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Section Intérêt — Oui / Non */}
        {sondage?.sonderInteret && (
          <Card sx={{ mb: 3, borderLeft: 4, borderColor: interetOui === true ? "#4caf50" : interetOui === false ? "#f44336" : "#e0e0e0" }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Êtes-vous intéressé(e) par cette formation ?
              </Typography>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <Button
                  variant={interetOui === true ? "contained" : "outlined"}
                  size="large"
                  startIcon={<ThumbsUp size={20} />}
                  onClick={() => setInteretOui(true)}
                  sx={{
                    minWidth: 140,
                    bgcolor: interetOui === true ? "#4caf50" : "transparent",
                    color: interetOui === true ? "white" : "#4caf50",
                    borderColor: "#4caf50",
                    fontWeight: interetOui === true ? 700 : 400,
                    "&:hover": { bgcolor: interetOui === true ? "#388e3c" : "#e8f5e9", borderColor: "#4caf50" },
                  }}
                >
                  Oui
                </Button>
                <Button
                  variant={interetOui === false ? "contained" : "outlined"}
                  size="large"
                  startIcon={<ThumbsDown size={20} />}
                  onClick={() => setInteretOui(false)}
                  sx={{
                    minWidth: 140,
                    bgcolor: interetOui === false ? "#f44336" : "transparent",
                    color: interetOui === false ? "white" : "#f44336",
                    borderColor: "#f44336",
                    fontWeight: interetOui === false ? 700 : 400,
                    "&:hover": { bgcolor: interetOui === false ? "#d32f2f" : "#fce4ec", borderColor: "#f44336" },
                  }}
                >
                  Non
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* ============================================================ */}
        {/* Option 2 — Préférences demi-journées (grille hebdomadaire)  */}
        {/* ============================================================ */}
        {(() => {
          const { preferences: prefCreneaux, datesPrecises: datesCreneaux } = splitCreneaux(creneaux);

          const JOURS = [
            { value: 1, label: "Lun", date: "2000-01-03" },
            { value: 2, label: "Mar", date: "2000-01-04" },
            { value: 3, label: "Mer", date: "2000-01-05" },
            { value: 4, label: "Jeu", date: "2000-01-06" },
            { value: 5, label: "Ven", date: "2000-01-07" },
          ];

          const cycleReponse = (creneauId: string) => {
            const current = reponses[creneauId];
            const next: ReponseValue = !current ? "OUI" : current === "OUI" ? "PEUT_ETRE" : current === "PEUT_ETRE" ? "NON" : "OUI";
            toggleReponse(creneauId, next);
          };

          const stateColors: Record<string, { bg: string; color: string; border: string; label: string }> = {
            OUI: { bg: "#e8f5e9", color: "#2e7d32", border: "#4caf50", label: "Oui" },
            PEUT_ETRE: { bg: "#fff8e1", color: "#f57f17", border: "#ff9800", label: "Peut-être" },
            NON: { bg: "#fce4ec", color: "#c62828", border: "#f44336", label: "Non" },
          };

          // Plages horaires : matin < 13h, après-midi 13h–18h, soirée ≥ 18h.
          // La grille s'ADAPTE : on n'affiche QUE les plages réellement
          // proposées. Sans la plage Soirée, un créneau du soir (ex. 19h)
          // tombait en « après-midi » et écrasait l'éventuel créneau d'après-midi
          // du même jour dans creneauMap → il disparaissait de la grille → aucune
          // case à cocher → réponse jamais complète → soumission bloquée.
          // (Bug créneaux soirée, 2026-06-24.)
          type Period = "M" | "A" | "S";
          const PERIOD_LABELS: Record<Period, string> = { M: "Matin", A: "Après-midi", S: "Soirée" };
          const PERIODS_ORDER: Period[] = ["M", "A", "S"];
          const periodOf = (heureDebut: string): Period =>
            heureDebut < "13:00" ? "M" : heureDebut < "18:00" ? "A" : "S";

          // Build creneaux map for preference grid
          const creneauMap: Record<string, Creneau> = {};
          const periodsPresentes = new Set<Period>();
          for (const c of prefCreneaux) {
            const jour = JOURS.find(j => j.date === c.dateCreneau);
            if (!jour) continue;
            const period = periodOf(c.heureDebut);
            creneauMap[`${jour.value}-${period}`] = c;
            periodsPresentes.add(period);
          }
          const periodsAffichees = PERIODS_ORDER.filter(p => periodsPresentes.has(p));

          return (
            <>
              {/* Légende (affichée si on a des créneaux) */}
              {creneaux.length > 0 && (
                <Box sx={{ display: "flex", gap: 2, mb: 2, justifyContent: "center" }}>
                  <Chip icon={<CheckCircle size={14} />} label="Disponible" size="small" sx={{ bgcolor: "#e8f5e9" }} />
                  <Chip icon={<HelpCircle size={14} />} label="Peut-être" size="small" sx={{ bgcolor: "#fff8e1" }} />
                  <Chip icon={<XCircle size={14} />} label="Indisponible" size="small" sx={{ bgcolor: "#fce4ec" }} />
                </Box>
              )}

              {/* Grille demi-journées */}
              {prefCreneaux.length > 0 && (
                <>
                  <Card sx={{ mb: 2 }}>
                    <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Demi-journées de préférence
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Indiquez pour chaque demi-journée de la semaine si vous êtes généralement disponible.
                        Cliquez sur chaque case pour changer votre réponse. Les séances seront ensuite réparties sur les créneaux les plus favorables.
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "60px repeat(5, 1fr)", sm: "85px repeat(5, 1fr)" }, gap: { xs: 0.5, sm: 0.75 }, alignItems: "center" }}>
                        <Box />
                        {JOURS.map((j) => (
                          <Typography key={j.value} variant="caption" sx={{ textAlign: "center", fontWeight: 600 }}>
                            {j.label}
                          </Typography>
                        ))}

                        {/* Lignes par plage horaire — uniquement celles proposées */}
                        {periodsAffichees.map((period) => (
                          <Fragment key={period}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {PERIOD_LABELS[period]}
                            </Typography>
                            {JOURS.map((j) => {
                              const key = `${j.value}-${period}`;
                              const c = creneauMap[key];
                              if (!c) return <Box key={key} />;
                              const state = reponses[c.id];
                              const sc = state ? stateColors[state] : null;
                              return (
                                <Box
                                  key={key}
                                  onClick={() => cycleReponse(c.id)}
                                  sx={{
                                    py: 1.5,
                                    borderRadius: 1,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    bgcolor: sc ? sc.bg : "grey.50",
                                    color: sc ? sc.color : "text.disabled",
                                    border: "2px solid",
                                    borderColor: sc ? sc.border : "grey.300",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    userSelect: "none",
                                    transition: "all 0.15s",
                                    "&:hover": { opacity: 0.8, transform: "scale(1.03)" },
                                  }}
                                >
                                  {sc ? sc.label : "—"}
                                </Box>
                              );
                            })}
                          </Fragment>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* ============================================================ */}
              {/* Option 3 — Dates précises (créneaux spécifiques)             */}
              {/* ============================================================ */}
              {datesCreneaux.length > 0 && (
                <>
                  <Card sx={{ mb: 2 }}>
                    <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Sélection de créneaux : renseignez votre disponibilité
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Indiquez pour chaque créneau proposé si vous êtes disponible, indisponible ou incertain(e).
                      </Typography>
                    </CardContent>
                  </Card>

                  {datesCreneaux.map((c) => {
                    const selected = reponses[c.id];
                    return (
                      <Card
                        key={c.id}
                        sx={{
                          mb: 1.5,
                          borderLeft: 4,
                          borderColor: selected === "OUI" ? "#4caf50" : selected === "NON" ? "#f44336" : selected === "PEUT_ETRE" ? "#ff9800" : "#e0e0e0",
                        }}
                      >
                        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {formatJourSemaineFR(c.dateCreneau)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {c.heureDebut} — {c.heureFin}
                                {c.lieu ? ` | ${c.lieu}` : ""}
                              </Typography>
                              {!sondage?.anonyme && (
                                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: "#4caf50" }}>
                                    {c.nbOui} oui
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "#ff9800" }}>
                                    {c.nbPeutEtre} ?
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            <Box sx={{ display: "flex", gap: 1 }}>
                              {(["OUI", "PEUT_ETRE", "NON"] as ReponseValue[]).map((val) => {
                                const isSelected = selected === val;
                                const colors: Record<string, { bg: string; color: string; border: string }> = {
                                  OUI: { bg: "#e8f5e9", color: "#2e7d32", border: "#4caf50" },
                                  PEUT_ETRE: { bg: "#fff8e1", color: "#f57f17", border: "#ff9800" },
                                  NON: { bg: "#fce4ec", color: "#c62828", border: "#f44336" },
                                };
                                const labels: Record<string, string> = { OUI: "Oui", NON: "Non", PEUT_ETRE: "Peut-être" };

                                return (
                                  <Button
                                    key={val}
                                    size="small"
                                    variant={isSelected ? "contained" : "outlined"}
                                    onClick={() => toggleReponse(c.id, val)}
                                    sx={{
                                      minWidth: { xs: 60, sm: 80 },
                                      bgcolor: isSelected ? colors[val].bg : "transparent",
                                      color: colors[val].color,
                                      borderColor: colors[val].border,
                                      fontWeight: isSelected ? 700 : 400,
                                      "&:hover": {
                                        bgcolor: colors[val].bg,
                                        borderColor: colors[val].border,
                                      },
                                    }}
                                  >
                                    {labels[val]}
                                  </Button>
                                );
                              })}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              )}
            </>
          );
        })()}

        {/* Progress bar */}
        {creneaux.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Progression
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {Object.keys(reponses).length}/{creneaux.length}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "#f0f0f0",
                "& .MuiLinearProgress-bar": {
                  bgcolor: progress === 100 ? "#4caf50" : "#4BB7A5",
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        )}

        {/* Submit */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Send size={18} />}
          sx={{
            bgcolor: "#4BB7A5",
            "&:hover": { bgcolor: "#3a9485" },
            py: 1.5,
            fontWeight: 700,
            fontSize: "1rem",
          }}
        >
          {submitting ? "Envoi en cours..." : destinataire?.aRepondu ? "Modifier mes réponses" : "Envoyer mes réponses"}
        </Button>
      </Box>
    </Box>
  );
}
